import { cx } from "@emotion/css";
import { faGlobe } from "@fortawesome/pro-regular-svg-icons/faGlobe";
import { faPlusCircle } from "@fortawesome/pro-regular-svg-icons/faPlusCircle";
import { faUserLock } from "@fortawesome/pro-regular-svg-icons/faUserLock";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, ListItemButton, ListItemText, TextField } from '@mui/material';
import { DRAWER_INFINITE_SCROLL_PAGE_SIZE, KEYBOARD_ARROW_DOWN, SEARCH_DELAY_DEBOUNCE, VK_ENTER } from 'app/appConstants';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcBoxDrawer, MprcIconFa, MprcSpinnerIndicator, MprcTextMuted } from 'components';
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonClearSearch } from "components/iconButtons/IconButtonClearSearch";
import MprcAutoSizer from 'components/MprcAutoSizer';
import { ExtractErrorInformation, MprcRequestError, MprcRequestSnackbarErrorReport, MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { GetUserListsResponse, PublicationListType } from 'generated/CoreApi_v1';
import { useSnackbar } from 'notistack';
import { isCanceledError, useCoreApi } from 'providers/CoreApiProvider';
import React, { startTransition, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { publish } from "views/knowledge/feed/FeedEvents";

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    onChanged: (items: GetUserListsResponse[]) => void;
    subtitle: string;
    onClose: () => void;
}

interface InputText {
    value: string;
    focused: boolean;
    error: boolean;
}

type ItemData = {
    items: GetUserListsResponse[];
    onSelectItem: (item: GetUserListsResponse) => void;
};

////////////////////////////////////////////////////////////////////////////////

/**
 * Renderiza uma linha da Lista.
 * @param props
 */
// Renderiza uma linha da Lista.
function RenderRow(props: ListChildComponentProps<ItemData>) {
    const { index, style, } = props;
    const { items, onSelectItem } = props.data;
    const { t } = useTranslation("knowledge");

    if (index > items.length - 1) {
        return (
            <Box style={style} sx={{ mt: 1 }}>
                <MprcSpinnerIndicator />
            </Box>
        );
    }

    const item = items[index];
    return (
        <div key={index} style={style}>
            <ListItemButton dense sx={{ px: 1, height: style.height }} onClick={() => onSelectItem(item)}>
                <Checkbox
                    edge="start"
                    checked={item.selected}
                    tabIndex={-1}
                    disableRipple
                />

                <ListItemText
                    primary={item.name}
                    primaryTypographyProps={
                        {
                            noWrap: true,
                            variant: "h6",
                            color: "muted.main",
                            marginRight: 1,
                            fontWeight: item.type == PublicationListType.Favorite || item.type == PublicationListType.SeeLater ? "bold" : undefined
                        }}
                />
                <MprcTextMuted>
                    <MprcIconFa
                        icon={item.isPublicList ? faGlobe : faUserLock}
                        title={item.isPublicList ? t("Feed.Card.Actions.AddToListDrawer.PublicListTooltip") : t("Feed.Card.Actions.AddToListDrawer.PrivateListTooltip")}
                    />
                </MprcTextMuted>
            </ListItemButton>
        </div>
    );
}

////////////////////////////////////////////////////////////////////////////////

export const AddToListDrawer: React.FC<IProps> = (props) => {

    const { publicationId, onClose, onChanged, subtitle } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [searchText, setSearchText] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [items, setItems] = useState<GetUserListsResponse[]>([]);
    const [text, setText] = useState<InputText>({ value: "", error: false, focused: false });
    const [changedItems, setChangedItems] = useState<string[]>([]);

    const currentPage = useRef<number>(1);
    const endOfRecords = useRef<boolean>(true);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);
    const hasMountedRef = useRef(false);
    const abortControllerRef = useRef<AbortController>(undefined);

    let searchTextRef = useRef<HTMLInputElement>(null);
    let inputTextRef = useRef<HTMLInputElement>(null);

    ///
    async function loadData() {

        try {

            abortControllerRef.current?.abort();

            abortControllerRef.current = new AbortController();

            var result = await api.v1.knowledgePublication.getUserLists({
                publicationId: publicationId,
                searchText: searchText,
                publicListsOnly: false,
                pageNumber: currentPage.current,
                pageSize: DRAWER_INFINITE_SCROLL_PAGE_SIZE
            }, { signal: abortControllerRef.current.signal });

            if (currentPage.current == 1) {
                // Reseta o cache.
                if (hasMountedRef.current) {
                    if (infiniteLoaderRef.current) {
                        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
                    }
                }
                // Inicializa o array de itens.
                setItems(result.data.items);
            }
            else {
                // Adiciona mais itens no array.
                setItems([...items, ...result.data.items]);
            }

            // Há mais registros?
            endOfRecords.current = result.data.items.length < DRAWER_INFINITE_SCROLL_PAGE_SIZE;

            setTimeout(() => searchTextRef.current?.focus(), 100);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                MprcRequestSnackbarErrorReport(e);
                endOfRecords.current = true;
            }
        }
        finally {
            setLoading(false);
        }
    }

    ///
    const updateRecord = (itemId: string) => {
        const newItems = items.map(i => {
            if (i.listId == itemId) i.selected = !i.selected;
            return i;
        });
        setItems([...newItems]);

        if (changedItems.indexOf(itemId) == -1) {
            setChangedItems([...changedItems, itemId]);
        }
    };

    ///
    const removeItem = (item: GetUserListsResponse) => {
        setSaving(true);
        startTransition(() => {
            api.v1.knowledgePublication.removeListItem({ listPublicationId: item.listId, itemPublicationId: publicationId })
                .then(result => {
                    if (result) {
                        updateRecord(item.listId);
                    }
                    else {
                        enqueueSnackbar(t("Feed.Card.Actions.AddToListDrawer.DeleteErrorMessage"), { variant: "error" });
                    }
                })
                .catch(e => {
                    let error = ExtractErrorInformation(e as MprcRequestError);
                    enqueueSnackbar(error.message, { variant: "error" });
                }).finally(() => {
                    setSaving(false);
                });
        });
    };

    ///
    const addItem = async (item: GetUserListsResponse) => {
        setSaving(true);
        startTransition(async () => {
            try {
                var result = await api.v1.knowledgePublication.addListItem({ listPublicationId: item.listId, itemPublicationId: publicationId });

                if (result) {
                    updateRecord(item.listId);
                }
                else {
                    enqueueSnackbar(t("Feed.Card.Actions.AddToListDrawer.AddErrorMessage"), { variant: "error" });
                }
            }
            catch (e) {
                var r = MprcResolveRequestErrors(e);
                const fieldErrors: string[] = [];
                Object.entries(r.fieldsWithError ?? {}).forEach(([key, value]) => value.forEach(v => fieldErrors.push(v)));

                if (fieldErrors.length > 0)
                    fieldErrors.forEach(msg => enqueueSnackbar(msg, { variant: "error" }));
                else
                    MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setSaving(false);
            }
        });
    };

    ///
    const handleClose = () => {
        changedItems.forEach(itemId => publish("ListReload", itemId));
        onChanged(items);
        onClose();
    };

    // Ao selecionar um item da lista.
    const onSelectItem = (item: GetUserListsResponse) => {
        if (item.selected) removeItem(item);
        else addItem(item);
    };

    ///
    const loadNextPage = (startIndex: number, stopIndex: number) => {
        currentPage.current++;
        return new Promise<void>((resolve) => {
            loadData().then(() => resolve());
        });
    };

    ///
    const resetSearch = () => {
        currentPage.current = 1;
        setLoading(true);
        loadData();
        hasMountedRef.current = true;
    };

    // Se há mais itens para ser carregado, adicionamos UMA LINHA para que o Indicador de Carregamento apareça.
    const itemCount = !endOfRecords.current ? items.length + 1 : items.length;

    // Permite apenas uma página de items pode vez.
    const loadMoreItems = loading ? () => { } : loadNextPage;

    // Verifica se a linha está carregada, exceto o Indicador de Carregamento.
    const isItemLoaded = (index: number) => endOfRecords.current || index < items.length;

    ///
    const handleAddList = () => {

        if (!text.value || text.value.length == 0) {
            setText({ ...text, error: true });
            return;
        }

        setSaving(true);
        api.v1.knowledgePublication.createPrivateList({ itemId: publicationId, title: text.value })
            .then((result) => {

                if (result.data.success && result.data.newPublicationId) {
                    enqueueSnackbar(t("Feed.Card.Actions.AddToListDrawer.AddNewListMessage", { name: text.value }), { variant: "success" });
                    onClose();
                }
                else {
                    enqueueSnackbar(t("Feed.Card.Actions.AddToListDrawer.CreateListErrorMessage"));
                }
            })
            .catch(e => {
                let error = ExtractErrorInformation(e as MprcRequestError);
                enqueueSnackbar(error.message, { variant: "error" });
            }).finally(() => setSaving(false));

    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    /// Executa a pesquisa depois de 600ms.
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            resetSearch();
        }, SEARCH_DELAY_DEBOUNCE);

        return () => {
            clearTimeout(delayDebounce);
        };
    }, [searchText]);

    return (
        <MprcBoxDrawer
            onClose={handleClose}
            title={t("Feed.Card.Actions.AddToListDrawer.Title")}
            subtitle={subtitle}
            requestIndicator={saving ? "post" : undefined}
            footerFullWidth
            header={
                <TextField
                    autoFocus
                    fullWidth
                    autoComplete="off"
                    placeholder={t("Feed.Card.Actions.AddToListDrawer.SearchTextPlaceholder")}
                    value={searchText}
                    onChange={() => {
                        setSearchText(searchTextRef.current?.value ?? "");
                    }}
                    onKeyDown={(e) => {
                        if (e.code == VK_ENTER) resetSearch();
                        else if (e.keyCode == KEYBOARD_ARROW_DOWN && searchTextRef.current?.value.length == 0) resetSearch();
                    }}
                    inputRef={searchTextRef}
                    slotProps={{
                        input: {
                            endAdornment:
                                searchText != "" &&
                                <IconButtonClearSearch
                                    size="small"
                                    onClick={() => setSearchText("")}
                                />
                        }
                    }}
                />
            }
            content={
                loading
                    ? <MprcSpinnerIndicator />
                    : !loading && itemCount == 0
                        ? <MprcFilterEmptyDataset message={t("Feed.Card.Actions.AddToListDrawer.NoRecordsFoundMessage")} />
                        : <MprcAutoSizer>
                            {
                                ({ width, height }) =>
                                    <InfiniteLoader
                                        isItemLoaded={isItemLoaded}
                                        itemCount={itemCount}
                                        loadMoreItems={loadMoreItems}
                                        ref={infiniteLoaderRef}
                                        threshold={10}
                                        minimumBatchSize={10}
                                    >
                                        {({ onItemsRendered, ref }) => (
                                            <FixedSizeList
                                                height={height}
                                                width={width}
                                                itemData={{ onSelectItem, items: items }}
                                                itemCount={itemCount}
                                                itemSize={40}
                                                onItemsRendered={onItemsRendered}
                                                ref={ref}
                                                className={cx(AppGlobalClassKeys.drawerFixedSizeList, AppGlobalClassKeys.thinScrollbar)}
                                            >
                                                {RenderRow}
                                            </FixedSizeList>
                                        )}
                                    </InfiniteLoader>
                            }
                        </MprcAutoSizer>
            }
            footer={
                <div style={{ width: "100%" }}>
                    <TextField
                        fullWidth
                        placeholder={!text.focused ? t("Feed.Card.Actions.AddToListDrawer.AddPlaceholder") : t("Feed.Card.Actions.AddToListDrawer.AddFocusedPlaceholder")}
                        variant="outlined"
                        size="small"
                        value={text.value}
                        autoComplete="off"
                        ref={inputTextRef}
                        error={text.error}
                        onChange={(e) => setText({ ...text, value: e.target.value })}
                        onBlur={() => setText({ ...text, focused: false })}
                        onFocus={() => setText({ ...text, focused: true })}
                        onKeyDown={(e) => {
                            if (e.code == VK_ENTER) handleAddList();
                        }}
                        slotProps={{
                            input: {
                                endAdornment: text.focused ? <FontAwesomeIcon icon={faPlusCircle} /> : undefined
                            }
                        }}
                    >
                    </TextField>
                </div>
            }
        />
    );
};
